/// <reference types="vite/client" />
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * 新版本提示 / new-version banner — 改改 9/21 (fb_a50fe09d 「生成合同前未找到"调整合同额"按钮」).
 *
 * Her screenshot was the PRE-deploy layout: the tab had been open since before the deploy, so it was still
 * running the old bundle and the button she was told about did not exist in it. Nothing told her. This does:
 * on window focus and every 5 minutes it re-reads index.html (no-store) and compares the entry script
 * (`/assets/index-<hash>.js`) with the one this page booted from. A different hash = a deploy happened.
 *
 * Mounted once at the App root, so it covers the staff app, the labor portal and the public bid page alike.
 * Silent on any failure (offline, 502 mid-deploy, a login redirect) — the next focus / tick simply tries
 * again, and a check never re-fires itself. Never runs in dev (Vite serves /src/main.tsx, no hash to compare).
 */

const ENTRY_SELECTOR = 'script[type="module"][src*="/assets/index-"]';
const POLL_MS = 5 * 60_000;
/** focus can fire in bursts (alt-tab, devtools, a file picker closing) — one check per 30 s is plenty. */
const MIN_GAP_MS = 30_000;

/** `/assets/index-MLFFZ0ip.js` — the pathname of the entry script, or null when there is none to compare. */
function entryOf(doc: Document): string | null {
  const src = doc.querySelector(ENTRY_SELECTOR)?.getAttribute('src');
  if (!src) return null;
  try { return new URL(src, window.location.origin).pathname; } catch { return null; }
}

/** The bundle this page booted from — read once, from the live DOM, before anything could change it. */
const BOOT_ENTRY: string | null = typeof document !== 'undefined' ? entryOf(document) : null;

async function latestEntry(): Promise<string | null> {
  const res = await fetch('/', { cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'text/html' } });
  if (!res.ok) return null;
  const type = res.headers.get('content-type') ?? '';
  if (type && !type.includes('text/html')) return null;
  const html = await res.text();
  return entryOf(new DOMParser().parseFromString(html, 'text/html'));
}

export default function NewVersionBanner() {
  const [stale, setStale] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (import.meta.env.DEV || !BOOT_ENTRY) return;
    let inFlight = false;
    let lastAt = 0;
    let done = false;

    async function check(force = false) {
      if (done || inFlight) return;
      const now = Date.now();
      if (!force && now - lastAt < MIN_GAP_MS) return;
      inFlight = true; lastAt = now;
      try {
        const latest = await latestEntry();
        // null = the answer had no entry script we can read (an error page, a proxy's HTML) — not a deploy.
        if (latest && latest !== BOOT_ENTRY) { done = true; setStale(true); }
      } catch {
        // offline / mid-deploy / aborted — silent; the next focus or tick tries once more.
      } finally { inFlight = false; }
    }

    const onFocus = () => { void check(); };
    const onVisible = () => { if (document.visibilityState === 'visible') void check(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(() => { void check(true); }, POLL_MS);
    return () => {
      done = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, []);

  // The bar is fixed; push the page (and the sticky header) down by its real height, which wraps on a phone.
  useLayoutEffect(() => {
    const el = barRef.current;
    const root = document.documentElement;
    if (!stale || !el) return;
    const apply = () => root.style.setProperty('--update-banner-h', `${el.offsetHeight}px`);
    apply();
    document.body.classList.add('has-update-banner');
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      document.body.classList.remove('has-update-banner');
      root.style.removeProperty('--update-banner-h');
    };
  }, [stale]);

  if (!stale) return null;
  return (
    <div ref={barRef} className="update-banner" role="status" aria-live="polite">
      <span>系统已更新，请刷新页面以使用新版本 / A new version is available</span>
      <button type="button" onClick={() => window.location.reload()}>刷新 / Refresh</button>
    </div>
  );
}
